import { CustomError, assertNever, assertValid, die, toDebugJson } from '@yp/common/core.ts';
import {
  isAbsent,
  isArray,
  isBoolean,
  isEmpty,
  isNumber,
  isObject,
  isPresent,
  isString,
  mapPartialRecord,
  pare,
  recordEntries,
  recordFromEntries,
} from '@yp/common/identity.ts';
import { asNumber } from '@yp/common/math.ts';
import { defaultProp } from '@yp/common/schema/build.ts';
import {
  hasEnumValue,
  type AnyTypeDescriptor,
  type Descriptor,
  type StrictPropertyList,
  type Struct,
  type TypeDescriptor,
} from '@yp/common/schema/descriptor.ts';
import { DateTime, parseStandardTime, toStandardTimeString } from '@yp/common/time.ts';
import { Scalar } from '@yp/common/units.ts';
import type {
  DocumentData,
  DocumentReference,
  FieldValue,
  PartialWithFieldValue,
  SetOptions,
  SnapshotOptions,
  Timestamp,
  WithFieldValue,
} from 'firebase/firestore';

interface TimestampLike {
  toMillis(): number;
}

type FireClasses = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly docRef: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly fieldVal: any;
  readonly ts: new (s: number, ns: number) => TimestampLike;
  readonly tsFromMillis: (millis: number) => TimestampLike;
};

// DNS: Can we remove this in favor of just using FireClasses?
let isAdminFieldValue: (v: unknown) => boolean = () => false;

// Although the code in this module is shared between web- and admin- firebase, we can't import
// firebase-admin/firestore in the same module without making Vite mad. So, we allow the admin
// implementation provide some necessary details.
export function setIsAdminFieldValue(predicate: (v: unknown) => boolean) {
  isAdminFieldValue = predicate;
}

export function isFieldValue(v: unknown, fclazz: FireClasses): v is FieldValue {
  return v instanceof fclazz.fieldVal || isAdminFieldValue(v);
}

type DataType =
  | FieldValue
  | boolean
  | string
  | number
  | Timestamp
  | DocumentReference
  | Array<DataType>
  | { [key: string]: DataType };

// type DataType2<D> = D extends Descriptor<infer T> ? T : never;

export class ConvertToFirestoreError extends CustomError {}

function propertyToFire(
  key: string,
  v: unknown,
  p: AnyTypeDescriptor,
  merge: boolean,
  fclazz: FireClasses,
): DataType | undefined {
  if (isFieldValue(v, fclazz)) {
    return v;
  }
  switch (p.t) {
    case 'bool':
      if (isBoolean(v)) {
        return v;
      }
      break;
    case 'number': {
      if (isString(v)) {
        const coerced = asNumber(v);
        if (isPresent(coerced)) {
          v = coerced;
        }
      }
      if (isNumber(v)) {
        if (!p.enum || hasEnumValue(p, v)) {
          return v;
        }
      }
      break;
    }
    case 'string':
      if (isString(v)) {
        if (!p.enum || hasEnumValue(p, v)) {
          return v;
        }
      }
      break;
    case 'datetime':
      if (v instanceof DateTime) {
        let d = v as DateTime;
        if (p.st === 'time') {
          return toStandardTimeString(d);
        }
        // if (p.st === 'date') {
        //   d = standardDateToLocal(d);
        // }
        return fclazz.tsFromMillis(d.toMillis()) as Timestamp;
      }
      break;
    case 'scalar':
      if (v instanceof Scalar && v.units.quantity === p.u.quantity) {
        return v.value(p.u);
      }
      break;
    case 'ref':
      if (v instanceof fclazz.docRef) {
        return v as DocumentReference;
      }
      break;
    case 'dict':
      if (isObject(v)) {
        return mapPartialRecord(v, (e, key) => {
          if (p.enum && !hasEnumValue(p, key)) {
            return undefined;
          }
          return propertyToFire(key as string, e, p.el as AnyTypeDescriptor, merge, fclazz);
        }) as DataType;
      }
      break;
    case 'array':
      if (isArray(v)) {
        return v
          .map((e) => propertyToFire(key, e, p.el as AnyTypeDescriptor, merge, fclazz))
          .filter(isPresent);
      }
      break;
    case 'obj':
      if (isObject(v)) {
        // TODO: Why is this excessively deep??
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return convertToFire(v as Struct, p.el, merge, fclazz);
      }
      break;
    default:
      assertNever(p);
  }
  if (isPresent(v)) {
    throw new ConvertToFirestoreError(
      `propertyToFire failed. ${key}: ${p.t} = <${typeof v}>${toDebugJson(v)}`,
      { key, type: p.t, value: v },
    );
  } else if (p.req === true && !merge) {
    throw new ConvertToFirestoreError(`propertyToFire failed. ${key} is required but absent.`, {
      key,
    });
  }
  return undefined;
}

// function index(key: string, value: PartialWithFieldValue<any>, merge: boolean) {
//   if (!merge || key in value) {
//     return value[key];
//   }
//   const kl = key.length;
//   const props = recordEntries(value)
//     .filter(([k]) => k.startsWith(key) && k[kl] === '.')
//     .map(([k, v]) => [k.slice(kl + 1), v] as const);
//   if (props.length) {
//     return recordFromEntries(props);
//   }
//   return undefined;
// }

function findProp(key: string, d: StrictPropertyList<any>): AnyTypeDescriptor | undefined {
  const keyPath = key.split('.');
  let i = 0;
  while (true) {
    const k = keyPath[i++];
    const prop = d.find((prop) => prop.k === k);
    if (i === keyPath.length || isAbsent(prop)) {
      return prop;
    }
    if (prop.t === 'obj') {
      d = prop.el;
      continue;
    }
    if (prop.t === 'dict') {
      // All dict keys match, so we don't need the keyPath component.
      if (++i === keyPath.length) {
        return prop.el as AnyTypeDescriptor;
      }
      if (prop.el.t === 'obj') {
        d = prop.el.el as StrictPropertyList<any>;
        continue;
      }
    }
    return undefined;
  }
}

export function convertToFire<T extends Struct>(
  obj: PartialWithFieldValue<T>,
  d: StrictPropertyList<T>,
  merge: boolean,
  fclazz: FireClasses,
): DataType | undefined {
  assertValid(obj, isObject);
  if (d.length === 1 && d[0].k === '') {
    die('Legacy code');
    d = Object.keys(obj).map((k) => ({ ...d[0], k })) as StrictPropertyList<T>;
  }
  // Previous implementation:
  // const entries = d.map(
  //   (p) => [p.k, propertyToFire(p.k, obj[p.k], p as AnyTypeDescriptor, merge, fclazz)] as const,
  // );
  // return pare(Object.fromEntries(entries));

  // Since strongly typed interfaces may have extra fields, we may receive safely typed objects for
  // conversion with spurious properties which can be silently ignored.
  const strict = false;
  const entries: Array<[string, DataType | undefined]> = [];
  for (const [k, v] of recordEntries(obj)) {
    const prop = findProp(k, d as StrictPropertyList<any>);
    if (isAbsent(prop)) {
      if (!strict) {
        continue;
      }
      throw new ConvertToFirestoreError(
        `convertToFire failed. ${k} not found in ${toDebugJson(d)}`,
        { k, d },
      );
    }
    const vc = propertyToFire(k, v, prop, merge, fclazz);
    entries.push([k, vc]);
  }
  return pare(recordFromEntries(entries)) as { [key: string]: DataType };
}

function propertyFromFire<T>(v: unknown, p: TypeDescriptor<T>, fclazz: FireClasses): unknown {
  switch (p.t) {
    case 'bool':
      if (isBoolean(v)) {
        return v;
      }
      break;
    case 'number':
      if (isNumber(v)) {
        if (!p.enum || hasEnumValue(p, v)) {
          return v;
        }
      }
      break;
    case 'string':
      if (isString(v)) {
        if (!p.enum || hasEnumValue(p, v)) {
          return v;
        }
      }
      break;
    case 'datetime':
      if (v instanceof fclazz.ts) {
        let dt = DateTime.fromMillis(v.toMillis());
        if (
          p.st === 'date' ||
          p.st === 'time' // deprecated
        ) {
          dt = dt.toUTC();
        }
        return dt;
      }
      if (p.st === 'time' && isString(v)) {
        return parseStandardTime(v);
      }
      break;
    case 'scalar':
      if (isNumber(v)) {
        return Scalar.from(p.u, v);
      }
      break;
    case 'ref':
      if (v instanceof fclazz.docRef) {
        return v;
      }
      break;
    case 'dict':
      if (isObject(v)) {
        return mapPartialRecord(v, (e, key) => {
          if (p.enum && !hasEnumValue(p, key)) {
            return undefined;
          }
          return propertyFromFire(e, p.el as AnyTypeDescriptor, fclazz) as {} | undefined;
        });
        // return mapRecord(v, (e) => propertyFromFire(e, p.el as AnyTypeDescriptor, fclazz));
      }
      break;
    case 'array':
      if (isArray(v)) {
        const result = v
          .map((e) => propertyFromFire(e, p.el as AnyTypeDescriptor, fclazz))
          .filter(isPresent);
        return p.req === true || result.length ? result : undefined;
      }
      break;
    case 'obj':
      if (isObject(v)) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const result = convertFromFire(v, p.el, fclazz);
        return p.req === true || !isEmpty(result) ? result : undefined;
      }
      break;
    default:
      assertNever(p);
  }
  return p.req === true ? defaultProp(p as AnyTypeDescriptor) : undefined;
}

export function convertFromFire<T extends Struct>(
  v: DocumentData,
  d: StrictPropertyList<T>,
  fclazz: FireClasses,
): T {
  if (d.length === 1 && d[0].k === '') {
    d = Object.keys(v).map((k) => ({ ...d[0], k })) as StrictPropertyList<T>;
  }
  return Object.fromEntries(
    d
      .map((p) => [p, propertyFromFire(v[p.k], p as AnyTypeDescriptor, fclazz)] as const)
      .filter(([p, v]) => p.req === true || isPresent(v))
      .map(([p, v]) => [p.k, v] as const),
  ) as T;
}

// export declare type WithValue<T, V> =
//   | T
//   | (T extends Primitive
//       ? T
//       : T extends {}
//         ? {
//             [K in keyof T]: WithValue<T[K], V> | V;
//           }
//         : never);
// export declare type PartialWithValue<T, V> =
//   | Partial<T>
//   | (T extends Primitive
//       ? T
//       : T extends {}
//         ? {
//             [K in keyof T]?: PartialWithValue<T[K], V> | V;
//           }
//         : never);

export interface QueryDocumentSnapshotLike {
  data(options: SnapshotOptions | undefined): DocumentData;
}

export class GeneralConverter<T extends Struct, QDS extends QueryDocumentSnapshotLike> {
  // implements FirestoreDataConverter<T>
  constructor(
    private readonly desc: Descriptor<T>,
    readonly fclazz: FireClasses,
  ) {}

  toFirestore(model: WithFieldValue<T>): DocumentData;
  toFirestore(model: PartialWithFieldValue<T>, options: SetOptions): DocumentData;
  toFirestore(model: PartialWithFieldValue<T>, options?: SetOptions): DocumentData {
    const merge =
      isPresent(options) && ('mergeFields' in options || ('merge' in options && options.merge));
    // TODO: Get rid of cast.
    return convertToFire(model, this.desc.props, merge, this.fclazz) as DocumentData;
  }

  fromFirestore(snapshot: QDS, options?: SnapshotOptions) {
    const data = snapshot.data(options);
    return convertFromFire(data, this.desc.props, this.fclazz);
  }
}

export const _testable = {
  propertyFromFire,
  propertyToFire,
};
